import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Percent, ChevronLeft, ChevronRight } from "lucide-react";
import { Slider } from "../slider";
import { isUsable } from "../../../helpers/functions";

const flattenToc = (toc = []) =>
  toc.reduce((acc, item) => {
    acc.push(item);
    if (item.subitems?.length) acc.push(...flattenToc(item.subitems));
    return acc;
  }, []);

const normalizeHref = (href = "") => href.replace(/^\.?\//, "");

const ProgressSheet = ({
  rendition,
  progress = 0,
  totalLocations = 0,
  chapterName = "",
  onSeek = () => {},
  onPrev = () => {},
  onNext = () => {},
  show = true,
}) => {
  const [open, setOpen] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [activeHref, setActiveHref] = useState("");
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!isUsable(rendition) || !isUsable(rendition.book)) return;
    rendition.book.loaded.navigation.then(({ toc }) => {
      setChapters(flattenToc(toc));
    });
  }, [rendition]);

  useEffect(() => {
    if (!isUsable(rendition)) return;

    const updateActiveHref = (location) => {
      const href = location?.start?.href;
      if (href) setActiveHref(href);
    };

    rendition.on("relocated", updateActiveHref);
    if (rendition.location) updateActiveHref(rendition.location);

    return () => rendition.off("relocated", updateActiveHref);
  }, [rendition]);

  useEffect(() => {
    if (!show) setOpen(false);
  }, [show]);

  if (!show) return null;

  const currentChapterIndex = chapters.findIndex(
    (item) => normalizeHref(item.href) === normalizeHref(activeHref),
  );
  const percent = Math.floor((progress * 100) / (totalLocations || 1)) || 0;

  return (
    <>
      <button
        type="button"
        className="progress-fab bottom-20 sm:bottom-10"
        onClick={() => setOpen(true)}
        aria-label="Reading progress"
      >
        <Percent className="size-5" strokeWidth={2.5} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="progress-sheet-backdrop"
              className="progress-sheet__backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              key="progress-sheet"
              ref={sheetRef}
              className="progress-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
            >
              <div className="progress-sheet__handle" />

              <div className="progress-sheet__title">Progress: {percent}%</div>

              <div className="progress-sheet__stats">
                <div className="progress-sheet__stat">
                  <span className="progress-sheet__stat__label">Pages</span>
                  <span className="progress-sheet__stat__value">
                    {(progress || 0) + 1}
                  </span>
                  <span className="progress-sheet__stat__sep">/</span>
                  <span className="progress-sheet__stat__value">
                    {totalLocations || 0}
                  </span>
                </div>

                {chapters.length > 0 && (
                  <div className="progress-sheet__stat">
                    <span className="progress-sheet__stat__label">
                      Chapters
                    </span>
                    <span className="progress-sheet__stat__value">
                      {currentChapterIndex >= 0 ? currentChapterIndex + 1 : "-"}
                    </span>
                    <span className="progress-sheet__stat__sep">/</span>
                    <span className="progress-sheet__stat__value">
                      {chapters.length}
                    </span>
                  </div>
                )}
              </div>

              <div className="progress-sheet__controls">
                <button
                  type="button"
                  className="progress-sheet__nav-btn"
                  onClick={onPrev}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="size-6" strokeWidth={2} />
                </button>

                <Slider
                  value={[progress]}
                  onValueChange={([v]) => onSeek({ target: { value: v } })}
                  max={totalLocations || 100}
                  step={1}
                  aria-label="Reading progress"
                />

                <button
                  type="button"
                  className="progress-sheet__nav-btn"
                  onClick={onNext}
                  aria-label="Next page"
                >
                  <ChevronRight className="size-6" strokeWidth={2} />
                </button>
              </div>

              {chapterName && (
                <div className="progress-sheet__chapter">{chapterName}</div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProgressSheet;
